import { CommonModule } from '@angular/common';
import { NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { RouterModule } from '@angular/router';
import { JwtModule } from '@auth0/angular-jwt';
import { environment as env } from '@macs-frontend/env';
import { SharedModule } from '@macs-frontend/web-macs/shared/shared.module';
import { UIKitModule } from '@macs-frontend/web/features/ui-kit';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { NgrxFormsModule } from 'ngrx-forms';
import { routes } from './auth.routes';
import { DebugTokenComponent } from './containers/debug-token/debug-token.component';
import { LoginComponent } from './containers/login/login.component';
import { ResetPasswordComponent } from './containers/reset-password/reset-password.component';
import { SendEmailComponent } from './containers/send-email/send-email.component';
import { WrapperComponent } from './containers/wrapper/wrapper.component';
import { AuthEffects } from './effects/auth.effects';
import { authReducer } from './reducers/';
import { AuthService } from './services/auth.service';

/**
 * Legacy json_login token, for builds where Keycloak is switched off.
 *
 * Under Keycloak this MUST return null. JwtModule installs a global
 * JwtInterceptor, and @auth0/angular-jwt allows every SAME-ORIGIN request
 * regardless of `allowedDomains` (isAllowedDomain returns true as soon as the
 * request host matches the page host, and all our API URLs are relative). So
 * any browser still holding a `token` from the pre-Keycloak era had that
 * Lexik JWT stamped onto every single API call — and TokenInterceptor, which
 * respects a pre-set Authorization header, then skipped attaching the
 * Keycloak one.
 *
 * The backend saw a bearer with no `kid` (Keycloak always sets one, Lexik
 * never does), tried every Keycloak key, and answered 401 "Signature
 * verification failed" — for that user only, on every request, permanently.
 */
export function tokenGetter(): string | null {
  return env.keycloak ? null : localStorage.getItem('token');
}

@NgModule({
  imports: [
    CommonModule,
    SharedModule,
    RouterModule.forChild(routes),
    UIKitModule,
    NgrxFormsModule,
    StoreModule.forFeature('auth', authReducer),
    EffectsModule.forFeature([AuthEffects]),
    // @ts-ignore - View Engine library without proper Ivy metadata
    JwtModule.forRoot({
      config: {
        tokenGetter,
        allowedDomains: ['macstest.app', 'macsdev.app', 'macs.app'],
      },
    }),
    MatSnackBarModule,
  ],
  declarations: [
    LoginComponent,
    SendEmailComponent,
    WrapperComponent,
    ResetPasswordComponent,
    DebugTokenComponent,
  ],
  providers: [AuthService],
  schemas: [NO_ERRORS_SCHEMA],
})
export class AuthModule {}
